/**
 * ExportDropdown.tsx
 * 
 * Dropdown para exportar contactos en múltiples formatos.
 * Soporta: JSON, CSV, XLSX, PDF, TXT
 * 
 * @version 1.0.0
 * @date 2024-11-06
 */

import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileCode,
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faFileAlt,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

import { useContactExport, ExportFormat } from '../../../hooks/useContactExport';
import { ContactDB } from '../../../data/mockContactsDatabase';
import styles from '../../../types/ContactsHubSpot.module.css';

// ==================== 📋 INTERFACES ====================

interface ExportDropdownProps {
  contacts: ContactDB[];
  filename?: string;
  onExportComplete?: (format: ExportFormat, count: number) => void;
}

interface FormatOption {
  format: ExportFormat;
  label: string;
  icon: any;
  color: string;
  description: string;
}

// ==================== 📋 FORMAT OPTIONS ====================

const FORMAT_OPTIONS: FormatOption[] = [
  {
    format: 'json',
    label: 'JSON',
    icon: faFileCode,
    color: '#f0db4f',
    description: 'Formato estructurado para APIs'
  },
  {
    format: 'csv',
    label: 'CSV',
    icon: faFileCsv,
    color: '#4caf50',
    description: 'Compatible con Excel y Google Sheets'
  },
  {
    format: 'xlsx',
    label: 'Excel (XLSX)',
    icon: faFileExcel,
    color: '#217346',
    description: 'Archivo nativo de Microsoft Excel'
  },
  {
    format: 'pdf',
    label: 'PDF',
    icon: faFilePdf,
    color: '#f40f02',
    description: 'Documento portable (vista previa)'
  },
  {
    format: 'txt',
    label: 'Texto (TXT)',
    icon: faFileAlt,
    color: '#607d8b',
    description: 'Formato de texto plano'
  }
];

// ==================== 🎨 COMPONENT ====================

const ExportDropdown: React.FC<ExportDropdownProps> = ({
  contacts,
  filename,
  onExportComplete
}) => {
  
  const { exportContacts } = useContactExport();
  const [lastExported, setLastExported] = useState<ExportFormat | null>(null);
  
  /**
   * Manejar exportación
   */
  const handleExport = (format: ExportFormat) => {
    try {
      exportContacts(contacts, {
        format,
        filename,
        includeHeaders: true
      });
      
      setLastExported(format);
      
      // Reset después de 2 segundos
      setTimeout(() => {
        setLastExported(null);
      }, 2000);
      
      // Callback
      if (onExportComplete) {
        onExportComplete(format, contacts.length);
      }
      
    } catch (error) {
      console.error('Error exportando:', error);
    }
  };
  
  return (
    <Dropdown>
      <Dropdown.Toggle
        as="button"
        className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <FontAwesomeIcon icon={faDownload} />
        Exportar
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: '300px' }}>
        <div style={{ 
          padding: '8px 12px', 
          borderBottom: '1px solid var(--hs-gray-200)',
          marginBottom: '4px'
        }}>
          <small style={{ 
            fontWeight: 600, 
            fontSize: '0.75rem', 
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'var(--hs-gray-600)'
          }}>
            Seleccionar Formato
          </small>
          <div style={{ 
            fontSize: '0.75rem', 
            color: 'var(--hs-gray-600)',
            marginTop: '4px'
          }}>
            {contacts.length} contactos
          </div>
        </div>
        
        {FORMAT_OPTIONS.map(option => (
          <Dropdown.Item
            key={option.format}
            onClick={() => handleExport(option.format)}
            style={{
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease'
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '6px',
                backgroundColor: `${option.color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FontAwesomeIcon 
                icon={option.icon}
                style={{ color: option.color, fontSize: '18px' }}
              />
            </div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontWeight: 600, 
                fontSize: '0.875rem',
                marginBottom: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {option.label}
                {lastExported === option.format && (
                  <FontAwesomeIcon 
                    icon={faCheckCircle}
                    style={{ 
                      color: 'var(--hs-success)',
                      fontSize: '14px',
                      animation: 'fadeIn 0.2s ease'
                    }}
                  />
                )}
              </div>
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--hs-gray-600)',
                lineHeight: 1.3
              }}>
                {option.description}
              </div>
            </div>
          </Dropdown.Item>
        ))}
        
        <div style={{ 
          padding: '8px 12px', 
          borderTop: '1px solid var(--hs-gray-200)',
          marginTop: '4px'
        }}>
          <small style={{ 
            fontSize: '0.688rem', 
            color: 'var(--hs-gray-600)',
            display: 'block',
            lineHeight: 1.4
          }}>
            💡 <strong>Tip:</strong> Los archivos Excel y PDF pueden abrirse directamente para vista previa.
          </small>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ExportDropdown;